import { buildIdeariaHeaders } from "./identity-header";

export type Permission = {
  id?: string;
  name?: string;
  fkUser?: string;
  fkTenant?: string;
  role?: number;
  FkTenant?: string;
};

export type AuthUser = {
  id: string;
  username: string;
  email?: string | null;
  phoneNumber?: string | null;
  preferredRole?: number | null;
  permissions?: Permission[];
  token: string;
  picture?: string | null;
};

type LoginResponse = Partial<AuthUser> & {
  Id?: string;
  Username?: string;
  Email?: string | null;
  PhoneNumber?: string | number | null;
  PreferredRole?: number | null;
  Permissions?: Permission[];
  Token?: string;
  Picture?: string | null;
};

const API_BASE = `${import.meta.env.VITE_OSTEOPATIA_SERVER ?? "http://localhost:5054"}/api`;
const USER_STORAGE_KEY = "osteopatia_user";
export const AUTH_REQUIRED_EVENT = "osteopatia:auth-required";

export type AuthRequiredDetail = {
  returnUrl: string;
};

export function getSafeAuthReturnUrl(returnUrl?: string | null) {
  if (!returnUrl?.startsWith("/") || returnUrl.startsWith("//")) return "/dashboard";
  if (returnUrl === "/login" || returnUrl.startsWith("/login?")) return "/dashboard";
  return returnUrl;
}

export function requestAuthentication(returnUrl?: string) {
  clearStoredUser();
  if (typeof window === "undefined") return;

  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.dispatchEvent(
    new CustomEvent<AuthRequiredDetail>(AUTH_REQUIRED_EVENT, {
      detail: { returnUrl: getSafeAuthReturnUrl(returnUrl ?? currentUrl) },
    }),
  );
}

function normalizeUser(raw: LoginResponse): AuthUser {
  const token = raw.token ?? raw.Token;
  const id = raw.id ?? raw.Id;
  const username = raw.username ?? raw.Username;

  if (!token || !id || !username) {
    throw new Error("La respuesta de autenticacion no tiene el formato esperado.");
  }

  return {
    id,
    username,
    email: raw.email ?? raw.Email ?? null,
    phoneNumber:
      raw.phoneNumber != null
        ? String(raw.phoneNumber)
        : raw.PhoneNumber != null
          ? String(raw.PhoneNumber)
          : null,
    preferredRole: raw.preferredRole ?? raw.PreferredRole ?? null,
    permissions: raw.permissions ?? raw.Permissions ?? [],
    token,
    picture: raw.picture ?? raw.Picture ?? null,
  };
}

async function callApi<T>(
  endpoint: string,
  method = "GET",
  body?: unknown,
  token?: string,
  requestLoginOnUnauthorized = false,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) headers.Authorization = `Bearer ${token}`;

  const tenantId = getStoredTenantId();
  if (tenantId) headers["X-Tenant-Id"] = tenantId;

  const headersWithIdearia = await buildIdeariaHeaders(headers);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      method,
      headers: headersWithIdearia,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(String(error));
  }

  const raw = await response.text();
  const data = raw ? parseJson(raw) : undefined;

  if (!response.ok) {
    if (response.status === 401) {
      if (requestLoginOnUnauthorized) requestAuthentication();
      else clearStoredUser();
    }
    throw new Error(getApiErrorMessage(data));
  }

  return data as T;
}

function parseJson(raw: string) {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function getApiErrorMessage(data: unknown) {
  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    const value = data as Record<string, unknown>;
    for (const key of ["message", "Message", "detail", "Detail", "error", "Error"]) {
      if (typeof value[key] === "string") return value[key];
    }
  }
  return JSON.stringify(data);
}

export function getStoredUser(): AuthUser | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    return stored ? normalizeUser(JSON.parse(stored)) : null;
  } catch {
    clearStoredUser();
    return null;
  }
}

export function storeUser(user: AuthUser) {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

export function clearStoredUser() {
  if (typeof window !== "undefined") localStorage.removeItem(USER_STORAGE_KEY);
}

export function getStoredTenantId() {
  const user = getStoredUser();
  return user?.permissions?.[0]?.fkTenant ?? user?.permissions?.[0]?.FkTenant ?? null;
}

export const authApi = {
  async login(username: string, password: string) {
    const user = normalizeUser(
      await callApi<LoginResponse>("/Auth/login2", "POST", {
        key: username.trim(),
        secret: password,
        provider: 1,
      }),
    );
    storeUser(user);
    return user;
  },

  async loginWithGoogle(googleIdToken: string) {
    const user = normalizeUser(
      await callApi<LoginResponse>("/Auth/google", "POST", {
        googleIdToken,
        credential: googleIdToken,
        idToken: googleIdToken,
      }),
    );
    storeUser(user);
    return user;
  },

  async validate(token: string) {
    await callApi<void>("/Auth/validate", "POST", token, token, true);
  },
};


